import React, { useEffect, useRef, useState } from 'react';
import { Card, Header, Form as BaseForm, Input, withDefaultUseForm, Button } from '@optic-delight/ui-framework';
import 'react-datepicker/dist/react-datepicker.min.css';
import { Row, Col, Overlay, Tooltip } from 'react-bootstrap';
import './vault.css';
import { maxChar250 } from '../utilities/utilities';
import { useSearchParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import ClientActions from './client-actions';

const BaseForms = withDefaultUseForm(BaseForm);

const SecretGeneration = () => {
	const [showPasscodeVerificationModel, setShowPasscodeVerificationModel] = useState(true);
	const [showGeneratePasscodeLink, setShowGeneratePasscodeLink] = useState(true);
	const [showVerifyPasscodeModal, setShowVerifyPasscodeModal] = useState(false);
	const [showPasscodeSuccessMessage, setShowPasscodeSuccessMessage] = useState(false);
	const [passcode, setPasscode] = useState(null);
	const [passcodeEntered, setPasscodeEntered] = useState(false);
	const [disableRetryPasscode, setDisableRetryPasscode] = useState(true);
	const [passCodeSentEmail, setPassCodeSentEmail] = useState('');
	const [searchParams] = useSearchParams();

	const [newSecret, setNewSecret] = useState(null);
	const [newSecretGenerated, setNewSecretGenerated] = useState(false);
	const [show, setShow] = useState(false);
	const [showSecret, setShowSecret] = useState(false);
	const [showThankYouMessage, setShowThankYouMessage] = useState(false);
	const target = useRef(null);

	const dispatch = useDispatch();
	const generateOtpApiResponse = useSelector(state => state.clientReducer.generateOtpApiResponse);
	const generateSecretApiResponse = useSelector(state => state.clientReducer.generateSecretApiResponse);

	const showSuccessMessage = () => {
		setShowPasscodeVerificationModel(false);
		setShowSecret(false);
		setShowThankYouMessage(true);
	};

	useEffect(() => {
		if (generateOtpApiResponse && Object.keys(generateOtpApiResponse).length > 0) {
			setPassCodeSentEmail(generateOtpApiResponse.email);
			setShowVerifyPasscodeModal(true);
			setShowGeneratePasscodeLink(false);
			setShowPasscodeSuccessMessage(true);
			setTimeout(() => {
				setDisableRetryPasscode(false);
			}, 1000 * 60 * 10);
		}
	}, [generateOtpApiResponse]);

	useEffect(() => {
		if (generateSecretApiResponse && Object.keys(generateSecretApiResponse).length > 0) {
			setShowPasscodeVerificationModel(false);
			setShowThankYouMessage(false);
			setShowSecret(true);
			setNewSecretGenerated(true);
			setNewSecret(generateSecretApiResponse.secret);
		}
	}, [generateSecretApiResponse]);

	const generatePasscode = () => {
		const params = {
			cse: searchParams.get('cse')
		};
		dispatch({ type: ClientActions.API_POST_GENERATE_OTP, params: params });
	};

	const changePasscode = event => {
		let passcode = event.target.value;
		setPasscode(passcode);
		setPasscodeEntered(true);
	};

	const verifyPasscode = () => {
		let params = {
			cse: searchParams.get('cse')
		};
		let data = {
			passcode: passcode
		};
		dispatch({ type: ClientActions.API_PUT_GENERATE_SECRET, params: params, payload: data });
	};

	return (
		<div data-testid="passcodeVerification" className="Vault">
			{showPasscodeVerificationModel ? (
				<BaseForms noValidate="noValidate">
					<Card>
						<Card.Body>
							<Header className="py-4" as="h3">
								Passcode Verification
							</Header>
							<Row>
								{showGeneratePasscodeLink ? (
									<Col md="auto">
										<a href="#top" onClick={generatePasscode}>
											Click here to generate a passcode
										</a>
									</Col>
								) : (
									<></>
								)}
								{showPasscodeSuccessMessage ? (
									<Col md="auto">
										<p>The passcode was sent to {passCodeSentEmail}</p>
									</Col>
								) : (
									<></>
								)}
							</Row>
							{showVerifyPasscodeModal ? (
								<Row>
									<Input
										label="Passcode"
										id="passcode"
										name="passcode"
										onChange={changePasscode}
										value={passcode}
										required
										validators={[
											{
												name: 'maxChar250',
												validate: maxChar250,
												message: 'Must be 250 characters or less.'
											}
										]}
									/>
								</Row>
							) : (
								<></>
							)}
						</Card.Body>
						<Card.Footer>
							{showVerifyPasscodeModal ? (
								<Row className="justify-content-end mx-3 pb-5">
									<Col md="auto">
										<Button type="submit" disabled={!passcodeEntered} onClick={verifyPasscode} variant="primary">
											Verify
										</Button>
									</Col>
									<Col md="auto">
										<Button type="submit" disabled={!passcodeEntered} variant="primary">
											Cancel
										</Button>
									</Col>
									<Col md="auto">
										<p>
											Didn&apos;t receive a passcode within 10 minutes?&nbsp;
											{disableRetryPasscode ? (
												<a href="#top" className="disabledCursor" onClick={event => event.preventDefault()}>
													Click here for a new passcode
												</a>
											) : (
												<a href="#top" onClick={generatePasscode}>
													Click here for a new passcode
												</a>
											)}
										</p>
									</Col>
								</Row>
							) : (
								<></>
							)}
						</Card.Footer>
					</Card>
				</BaseForms>
			) : (
				<></>
			)}

			{showSecret ? (
				<BaseForms noValidate="noValidate">
					<Card>
						<Card.Body>
							<Header className="py-4" as="h3">
								Your New Secret
							</Header>
							<Row>
								<Col md="auto">
									<Input
										id="newSecret"
										name="newSecret"
										helptext="Your secret has been saved to the database.  Please copy it for your records and click close when finished."
										value={newSecret}
										type={'text'}
										disabled
									/>
								</Col>
							</Row>
							<Row>
								<Col md="auto">
									<CopyToClipboard text={newSecret}>
										<Button disabled={!newSecretGenerated} ref={target} onClick={() => setShow(!show)}>
											Copy secret to clipboard
										</Button>
									</CopyToClipboard>
								</Col>
								<Col md="auto">
									<Button type="submit" disabled={!newSecretGenerated} onClick={showSuccessMessage} variant="primary">
										Close
									</Button>
								</Col>
							</Row>

							<Overlay target={target.current} show={show} placement="bottom">
								{props => (
									<Tooltip id="overlay-example" {...props}>
										Copied!
									</Tooltip>
								)}
							</Overlay>
						</Card.Body>
					</Card>
				</BaseForms>
			) : (
				<></>
			)}

			{showThankYouMessage ? (
				<BaseForms noValidate="noValidate">
					<Card>
						<Card.Body>
							<Header className="py-4" as="h3">
								Thank you!
							</Header>
							<Row>
								<Col md="auto">
									<p>Your secret is now ready to be used and will work concurrently with your old secret until it expires.</p>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</BaseForms>
			) : (
				<></>
			)}
		</div>
	);
};

export default SecretGeneration;
